export type ColorPaletteOption = {
  label: string;
  /** The `#rrggbb` hex value that gets stored. */
  value: string;
  /** The design token to render instead of the hex. */
  token: string;
  /** The design token for text on top of `token`, readable in every theme. */
  foreground: string;
};

/**
 * The colors users can pick wherever the product offers a color choice. Keep
 * every color picker on this palette for consistency.
 */
export const COLOR_PALETTE = [
  {
    label: 'Pink',
    value: '#f4a5bd',
    token: 'var(--color-palette-primary-pink)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Red',
    value: '#ff8881',
    token: 'var(--color-palette-primary-red)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Orange',
    value: '#e79d65',
    token: 'var(--color-palette-primary-orange)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Yellow',
    value: '#e0c65c',
    token: 'var(--color-palette-primary-yellow)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Green',
    value: '#a6d387',
    token: 'var(--color-palette-primary-green)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Turquoise',
    value: '#44ccc1',
    token: 'var(--color-palette-primary-turquoise)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Blue',
    value: '#86b8ff',
    token: 'var(--color-palette-primary-blue)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'Purple',
    value: '#d29af6',
    token: 'var(--color-palette-primary-purple)',
    foreground: 'var(--color-palette-primary-label)',
  },
  {
    label: 'White',
    value: '#ffffff',
    // UAD has no palette token for white yet.
    token: 'var(--color-linear-neutral-100)',
    // neutral-100 is white in every theme, so it always needs dark text.
    foreground: 'var(--color-linear-neutral-10)',
  },
] as const satisfies readonly ColorPaletteOption[];

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export const isHexColor = (color: string | null | undefined): color is string =>
  Boolean(color && HEX_COLOR_PATTERN.test(color));

export const findPaletteColor = (
  color: string | null | undefined,
): ColorPaletteOption | undefined =>
  color
    ? COLOR_PALETTE.find(
        (paletteColor) => paletteColor.value === color.toLowerCase(),
      )
    : undefined;

/**
 * Resolves a stored `#rrggbb` color to the CSS color to render: the matching
 * palette token when there is one, otherwise the hex value itself. Returns
 * `undefined` for no color or an invalid value.
 */
export const resolvePaletteColor = (
  color: string | null | undefined,
): string | undefined => {
  if (!isHexColor(color)) {
    return undefined;
  }
  return findPaletteColor(color)?.token ?? color;
};

/**
 * Resolves the text color to render on top of a stored palette color. Returns
 * `undefined` for a color outside the palette, so the caller's default text
 * color applies.
 */
export const resolvePaletteForeground = (
  color: string | null | undefined,
): string | undefined => findPaletteColor(color)?.foreground;
